import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, FileText, Merge, Trash2, RefreshCw, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DataCleanup() {
  const navigate = useNavigate();

  // --- Agency duplicates ---
  const [agencyGroups, setAgencyGroups] = useState(null);
  const [scanningAgencies, setScanningAgencies] = useState(false);
  const [primaryByKey, setPrimaryByKey] = useState({});
  const [mergingKey, setMergingKey] = useState(null);

  // --- Invoice duplicates ---
  const [invoiceDupes, setInvoiceDupes] = useState(null);
  const [scanningInvoices, setScanningInvoices] = useState(false);
  const [removingInvoices, setRemovingInvoices] = useState(false);

  const scanAgencies = async () => {
    setScanningAgencies(true);
    try {
      const res = await axios.get(`${API}/agency-duplicates`);
      setAgencyGroups(res.data.groups || []);
      const defaults = {};
      (res.data.groups || []).forEach((g) => { defaults[g.key] = g.agencies[0]?.id; });
      setPrimaryByKey(defaults);
    } catch (e) {
      toast.error('Failed to scan for duplicate agencies');
    } finally {
      setScanningAgencies(false);
    }
  };

  const mergeGroup = async (group) => {
    const primaryId = primaryByKey[group.key];
    if (!primaryId) { toast.error('Pick a primary agency to keep'); return; }
    const duplicateIds = group.agencies.map((a) => a.id).filter((id) => id !== primaryId);
    if (duplicateIds.length === 0) return;
    if (!window.confirm(`Merge ${duplicateIds.length} duplicate(s) into the selected agency? Their invoices and notes will move over and the duplicates will be deleted.`)) return;
    setMergingKey(group.key);
    try {
      const res = await axios.post(`${API}/agency-merge`, { primary_id: primaryId, duplicate_ids: duplicateIds });
      toast.success(`Merged: ${res.data.invoices_repointed} invoices re-pointed, ${res.data.agencies_removed} duplicates removed`);
      scanAgencies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMergingKey(null);
    }
  };

  const scanInvoices = async () => {
    setScanningInvoices(true);
    try {
      const res = await axios.get(`${API}/invoices/duplicates`);
      setInvoiceDupes(res.data);
    } catch (e) {
      toast.error('Failed to scan for duplicate invoices');
    } finally {
      setScanningInvoices(false);
    }
  };

  const removeInvoiceDupes = async () => {
    const n = invoiceDupes?.removable_count || 0;
    if (n === 0) return;
    if (!window.confirm(`Remove ${n} duplicate invoice${n !== 1 ? 's' : ''}? One copy of each is kept (verified copy preferred). This cannot be undone.`)) return;
    setRemovingInvoices(true);
    try {
      const res = await axios.post(`${API}/invoices/remove-duplicates`, {});
      toast.success(`Removed ${res.data.removed} duplicate invoice${res.data.removed !== 1 ? 's' : ''}`);
      scanInvoices();
    } catch (e) {
      toast.error('Failed to remove duplicates');
    } finally {
      setRemovingInvoices(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="data-cleanup-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/invoices')} data-testid="data-cleanup-back">
          <ArrowLeft size={16} className="mr-1" /> Back to Invoices
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Data Cleanup</h1>
          <p className="text-muted-foreground mt-1 text-sm">Merge duplicate agencies and remove duplicate invoices.</p>
        </div>
      </div>

      {/* Duplicate Agencies */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="text-primary" size={20} />
            <h2 className="text-lg font-semibold">Duplicate Agencies</h2>
          </div>
          <Button onClick={scanAgencies} disabled={scanningAgencies} variant="outline" data-testid="scan-agencies-btn">
            <RefreshCw size={14} className={`mr-1 ${scanningAgencies ? 'animate-spin' : ''}`} />
            {scanningAgencies ? 'Scanning...' : 'Scan for Duplicates'}
          </Button>
        </div>

        {agencyGroups && agencyGroups.length === 0 && (
          <p className="text-sm text-muted-foreground">No duplicate agencies found. 🎉</p>
        )}

        {agencyGroups && agencyGroups.map((group) => (
          <div key={group.key} className="border rounded-lg p-4 space-y-3" data-testid={`agency-dup-group-${group.key}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{group.count} possible duplicates</p>
              <Button
                size="sm"
                onClick={() => mergeGroup(group)}
                disabled={mergingKey === group.key}
                data-testid={`merge-group-${group.key}`}
              >
                <Merge size={14} className="mr-1" /> {mergingKey === group.key ? 'Merging...' : 'Merge'}
              </Button>
            </div>
            <div className="space-y-2">
              {group.agencies.map((a) => (
                <label key={a.id} className="flex items-center gap-3 text-sm p-2 rounded hover:bg-muted/50 cursor-pointer">
                  <input
                    type="radio"
                    name={`primary-${group.key}`}
                    checked={primaryByKey[group.key] === a.id}
                    onChange={() => setPrimaryByKey((p) => ({ ...p, [group.key]: a.id }))}
                    data-testid={`primary-radio-${a.id}`}
                  />
                  <span className="font-medium">{a.agency_name}</span>
                  {a.phone && <span className="text-muted-foreground">{a.phone}</span>}
                  {a.state && <Badge variant="outline">{a.state}</Badge>}
                  <Badge variant="outline" className="ml-auto">{a.invoice_count} invoices</Badge>
                  {primaryByKey[group.key] === a.id && <Badge className="bg-green-600">Keep</Badge>}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertCircle size={12} /> Select the record to KEEP. All others merge into it.
            </p>
          </div>
        ))}
      </Card>

      {/* Duplicate Invoices */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="text-primary" size={20} />
            <h2 className="text-lg font-semibold">Duplicate Invoices</h2>
          </div>
          <Button onClick={scanInvoices} disabled={scanningInvoices} variant="outline" data-testid="scan-invoices-btn">
            <RefreshCw size={14} className={`mr-1 ${scanningInvoices ? 'animate-spin' : ''}`} />
            {scanningInvoices ? 'Scanning...' : 'Scan for Duplicates'}
          </Button>
        </div>

        {invoiceDupes && (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm">
              <Badge variant="outline">{invoiceDupes.duplicate_groups} duplicate groups</Badge>
              <Badge variant="outline" className="text-orange-600 border-orange-300">{invoiceDupes.removable_count} removable</Badge>
            </div>
            {invoiceDupes.removable_count > 0 ? (
              <>
                <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                  {invoiceDupes.groups.slice(0, 100).map((g) => (
                    <div key={g.key} className="p-3 text-sm">
                      <p className="font-medium">{g.invoices[0]?.agency_name} · #{g.invoices[0]?.invoice_number} · ${g.invoices[0]?.amount}</p>
                      <p className="text-xs text-muted-foreground">{g.count} copies — keeping 1{g.invoices[0]?.verified ? ' (verified)' : ''}</p>
                    </div>
                  ))}
                </div>
                <Button onClick={removeInvoiceDupes} disabled={removingInvoices} className="text-white bg-red-600 hover:bg-red-700" data-testid="remove-invoice-dupes-btn">
                  <Trash2 size={14} className="mr-1" /> {removingInvoices ? 'Removing...' : `Remove ${invoiceDupes.removable_count} Duplicates`}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No duplicate invoices found. 🎉</p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
